<template>
  <div id="app">

    <div v-if="!errorLoading && !articles">
      Loading...
    </div>

    <div v-if="errorLoading">
      Unable to retrieve product information, please try again later.
    </div>

    <div v-if="!errorLoading && articles">

      <router-view
        :articles="articles"
        :swatches="swatches"
        :inspiration-board="inspirationBoard"
        :filter-options="filterOptions"
        @load-filter-options="loadFilterOptions"
        @show-swatch-detail="showSwatchDetail"
        @remove-swatch-from-inspiration-board="removeSwatchFromInspirationBoard"
        @load-inspiration-board="loadInspirationBoard"
        @clear-inspiration-board="clearInspirationBoard"
        @set-slideout-article="setSlideoutArticle"
        @set-filter-options="setFilterOptions"
        ></router-view>

      <div class="modal modal-swatch" :class="{ 'is-active': modalSwatchOpen }">
        <div class="modal-card">
          <header>
            <div><a href="#close-modal" aria-label="close" @click.prevent="closeSwatchModal">X close</a></div>
          </header>
          <section v-if="!modalSwatchError && !modalSwatchData" class="modal-card-body">
            <p>Loading...</p>
          </section>
          <section v-if="modalSwatchError" class="modal-card-body">
            <p>Unable to retrieve swatch information</p>
          </section>
          <section v-if="modalSwatchData" class="modal-card-body">
            <div><img class="modal-swatch-image" :src="modalSwatchData.image"></div>
            <button class="modal-swatch-button-toggle"
              @click.prevent="toggleModalSwatchOnInspirationBoard()">
              {{ modalSwatchOnInspirationBoard ? 'Remove from' : 'Add to' }} my samples</button>
            <div class="modal-swatch-article-name-line">{{ modalSwatchData.article.name }}</div>
            <div class="modal-swatch-swatch-name-line">{{ modalSwatchData.name }}</div>
          </section>
        </div>
      </div>

      <article-slideout
        :articles="articles"
        :slideout-article-id="slideoutArticleId"
        @close="clearSlideoutArticle"
        @set-slideout-article="setSlideoutArticle"
        ></article-slideout>

    </div>

  </div>
</template>

<script>
export default {
  name: 'app',
  data() {
    return {
      errorLoading: false,
      articles: null,
      swatches: null,
      inspirationBoard: null,
      filterOptions: null,
      modalSwatchOpen: false,
      modalSwatchId: 0,
      modalSwatchData: null,
      modalSwatchError: false,
      slideoutArticleId: null,
    }
  },
  computed: {
    modalSwatchOnInspirationBoard() {
      return this.inspirationBoard.indexOf(this.modalSwatchId) >= 0
    },
  },
  created() {
    const request = new XMLHttpRequest()
    request.open('POST', '/api/get-product-data', true)
    request.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
    request.onload = () => {
      if (request.status >= 200 && request.status < 400) {
        // Success!
        const result = JSON.parse(request.responseText)
        if (result.articles && result.swatches) {
          this.articles = result.articles
          this.swatches = result.swatches
        } else {
          this.errorLoading = true
        }
      } else {
        // We reached our target server, but it returned an error
        this.errorLoading = true
      }
    }
    request.onerror = () => {
      // There was a connection error of some sort
      this.errorLoading = true
    }
    request.send('type=everything')

    this.inspirationBoard = JSON.parse(localStorage.getItem('inspirationBoard'))
    if (!Array.isArray(this.inspirationBoard)) {
      this.inspirationBoard = []
    }
  },
  methods: {
    addSwatchToInspirationBoard(id) {
      let swatchIds = JSON.parse(localStorage.getItem('inspirationBoard')) // ensure we're up-to-date
      if (!Array.isArray(swatchIds)) {
        swatchIds = []
      }
      if (swatchIds.indexOf(id) === -1) {
        swatchIds.push(id)
        localStorage.setItem('inspirationBoard', JSON.stringify(swatchIds))
      }
      this.inspirationBoard = swatchIds
    },
    closeSwatchModal() {
      this.modalSwatchOpen = false
    },
    loadFilterOptions() {
      const request = new XMLHttpRequest()
      request.open('GET', '/api/get-filter-data', true)
      request.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      request.onload = () => {
        if (request.status >= 200 && request.status < 400) {
          // Success!
          const result = JSON.parse(request.responseText)
          if (result.filters) {
            this.filterOptions = result.filters
          } else {
            this.errorLoading = true
          }
        } else {
          // We reached our target server, but it returned an error
          this.errorLoading = true
        }
      }
      request.onerror = () => {
        // There was a connection error of some sort
        this.errorLoading = true
      }
      request.send()
    },
    removeSwatchFromInspirationBoard(id) {
      let swatchIds = JSON.parse(localStorage.getItem('inspirationBoard')) // ensure we're up-to-date
      if (!Array.isArray(swatchIds)) {
        swatchIds = this.swatchIds
      }
      const idIndex = swatchIds.indexOf(id)
      if (idIndex >= 0) {
        swatchIds.splice(idIndex, 1)
        localStorage.setItem('inspirationBoard', JSON.stringify(swatchIds))
      }
      this.inspirationBoard = swatchIds
    },
    showSwatchDetail(id) {
      this.modalSwatchData = null
      this.modalSwatchError = false
      this.modalSwatchId = id
      this.modalSwatchOpen = true
      const request = new XMLHttpRequest()
      request.open('POST', '/api/get-product-data', true)
      request.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      request.onload = () => {
        if (request.status >= 200 && request.status < 400) {
          // Success!
          const result = JSON.parse(request.responseText)
          if (result.id == this.modalSwatchId) { // eslint-disable-line eqeqeq
            this.modalSwatchData = result
          } else {
            this.modalSwatchError = true
          }
        } else {
          // We reached our target server, but it returned an error
          this.modalSwatchError = true
        }
      }
      request.onerror = () => {
        // There was a connection error of some sort
        this.modalSwatchError = true
      }
      request.send(`type=swatch&id=${this.modalSwatchId}`)
    },
    toggleModalSwatchOnInspirationBoard() {
      return this.modalSwatchOnInspirationBoard
        ? this.removeSwatchFromInspirationBoard(this.modalSwatchData.id)
        : this.addSwatchToInspirationBoard(this.modalSwatchData.id)
    },
    loadInspirationBoard(swatchIds) {
      this.inspirationBoard = swatchIds
      localStorage.setItem('inspirationBoard', JSON.stringify(swatchIds))
      this.$router.push('/inspiration-board/local')
    },
    clearInspirationBoard() {
      this.inspirationBoard = []
      localStorage.setItem('inspirationBoard', '[]')
    },
    setSlideoutArticle(slideoutArticleId) {
      this.slideoutArticleId = slideoutArticleId
    },
    clearSlideoutArticle() {
      this.slideoutArticleId = null
    },
  },
}
</script>


// WEBPACK FOOTER //
// src/App.vue